import React from 'react';
import { ArtworkImageRecord, ArtworkImageRole } from '../../../domain/inventory/types/product-types';

interface MultiAssetManagerProps {
  images: ArtworkImageRecord[];
  onChange: (images: ArtworkImageRecord[]) => void;
}

const ROLES: Array<{ role: ArtworkImageRole; label: string }> = [
  { role: 'primary', label: 'Primary' },
  { role: 'wall', label: 'Wall View' },
  { role: 'framed', label: 'Framed' },
  { role: 'detail', label: 'Detail' },
];

export const MultiAssetManager: React.FC<MultiAssetManagerProps> = ({ images, onChange }) => {
  const handleUrlChange = (index: number, url: string) => {
    const next = [...images];
    next[index] = { ...next[index], url };
    onChange(next);
  };

  const handleRoleSelect = (index: number, role: ArtworkImageRole) => {
    const next = images.map((img, i) => {
      if (i === index) {
        return { ...img, role, isPrimary: role === 'primary' ? true : img.isPrimary };
      }
      if (role === 'primary') {
        return { ...img, isPrimary: false, role: img.role === 'primary' ? 'detail' : img.role };
      }
      return img;
    });
    onChange(next);
  };

  const handleSetPrimary = (index: number) => {
    const next = images.map((img, i) => ({
      ...img,
      isPrimary: i === index,
      role: (i === index ? 'primary' : (img.role === 'primary' ? 'detail' : img.role)) as ArtworkImageRole,
    }));
    onChange(next);
  };

  const handleToggleFit = (index: number) => {
    const next = [...images];
    const current = next[index].fitMode || 'contain';
    next[index] = { ...next[index], fitMode: current === 'contain' ? 'cover' : 'contain' };
    onChange(next);
  };

  const handleRemove = (index: number) => {
    if (images.length <= 1) return;
    const next = images.filter((_, i) => i !== index);
    if (!next.some((img) => img.isPrimary)) {
      next[0] = { ...next[0], isPrimary: true, role: 'primary' };
    }
    onChange(next);
  };

  const handleAdd = () => {
    onChange([
      ...images,
      { url: '', role: 'detail', isPrimary: false, fitMode: 'contain' },
    ]);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-textMuted">Media Assets ({images.length})</label>
        <button
          type="button"
          onClick={handleAdd}
          className="text-[11px] px-2.5 py-1 rounded bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 border border-sky-500/40 cursor-pointer font-semibold"
        >
          ➕ Add Asset
        </button>
      </div>

      {images.map((asset, idx) => (
        <div key={idx} className="p-3 rounded-lg bg-bgInput/50 border border-border flex flex-col gap-2.5">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono text-textMuted w-5">#{idx + 1}</span>
            <input
              type="url"
              required={idx === 0}
              value={asset.url}
              onChange={(e) => handleUrlChange(idx, e.target.value)}
              placeholder="https://images.unsplash.com/..."
              className="flex-1 px-2.5 py-1.5 rounded bg-bgInput border border-border text-white text-xs focus:outline-none focus:border-accent font-mono"
            />
            <button
              type="button"
              disabled={images.length <= 1}
              onClick={() => handleRemove(idx)}
              className="px-2 py-1 rounded text-red-400 hover:bg-red-500/20 border border-red-500/30 text-xs cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
              title="Remove asset"
            >
              ✕
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-textMuted font-bold mr-1">Role:</span>
              {ROLES.map(({ role, label }) => {
                const isActive = asset.role === role;
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => handleRoleSelect(idx, role)}
                    className={`text-[10px] px-2 py-0.5 rounded font-medium cursor-pointer transition-colors border ${
                      isActive
                        ? 'bg-sky-500 text-slate-950 border-sky-400 font-bold'
                        : 'bg-bgCard text-textMuted border-border hover:text-white'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleToggleFit(idx)}
                className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-textMuted hover:text-white border border-border cursor-pointer"
              >
                {asset.fitMode === 'cover' ? 'Fill Card (cover)' : 'Fit Artwork (contain)'}
              </button>

              {!asset.isPrimary && (
                <button
                  type="button"
                  onClick={() => handleSetPrimary(idx)}
                  className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/40 cursor-pointer font-medium"
                >
                  ⭐ Set Primary
                </button>
              )}
              {asset.isPrimary && (
                <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-bold">
                  ★ Primary
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
